import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff, Mail, Lock, Shield, Users, FileCheck, Upload, Calculator, Archive } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6971d173-a5de-4161-a279-02a6e01f22fc/artifacts/tz3z7h3d_Tax%20Software%20Logo.png";

export default function Login() {
  const { login, formatError, user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onExpired = () => setInfo("Your session expired. Please sign in again.");
    window.addEventListener("auth:session-expired", onExpired);
    return () => window.removeEventListener("auth:session-expired", onExpired);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      if (u?.license?.status === "expired") {
        navigate("/payment");
      } else {
        navigate("/app/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && typeof detail === "object" && detail?.error === "trial_expired") {
        setError(detail.message || "Your trial has ended. Please upgrade to continue.");
        setTimeout(() => navigate("/pricing", { state: { reason: "trial_expired", email: form.email } }), 1800);
        return;
      }
      if (err.response?.status === 403 && typeof detail === "object" && detail?.error === "email_not_verified") {
        setError(detail.message || "Please verify your email before signing in.");
        setTimeout(() => navigate(`/verify-email?email=${encodeURIComponent(form.email)}`), 1800);
        return;
      }
      setError(formatError(detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">

      {/* ── LEFT PANEL ── */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col p-10 text-white"
        style={{ background: "linear-gradient(135deg, #3730A3 0%, #2563EB 50%, #1D4ED8 100%)" }}
      >
        {/* Decorative orbital curves */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20" viewBox="0 0 800 900" preserveAspectRatio="none">
          <path d="M-100,600 Q300,100 700,600 T1100,600" fill="none" stroke="white" strokeWidth="1.5" />
          <path d="M-100,750 Q300,250 700,750 T1100,750" fill="none" stroke="white" strokeWidth="1" />
          <path d="M200,-50 Q500,300 200,700" fill="none" stroke="white" strokeWidth="1" />
        </svg>

        {/* Sparkles */}
        <div className="absolute inset-0 pointer-events-none">
          {[
            { top: "8%", left: "70%", size: 16 }, { top: "20%", left: "40%", size: 8 },
            { top: "45%", left: "88%", size: 12 }, { top: "30%", left: "82%", size: 6 },
            { top: "65%", left: "12%", size: 10 }, { top: "18%", left: "88%", size: 7 },
          ].map((s, i) => (
            <svg key={i} style={{ position: "absolute", top: s.top, left: s.left, width: s.size, height: s.size, opacity: 0.55 }} viewBox="0 0 20 20" fill="white">
              <path d="M10 0l2 7h7l-6 4 2 7-5-4-5 4 2-7-6-4h7z" />
            </svg>
          ))}
        </div>

        {/* Logo row + hero text side by side */}
        <div className="relative z-10 flex items-start gap-5 mb-1">
          <div className="flex-shrink-0 w-28 h-28 rounded-full overflow-hidden flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.08)", boxShadow: "0 0 0 3px rgba(255,255,255,0.15)" }}>
            <img
              src={LOGO_URL}
              alt="Todays Kidz Simple Sign-In"
              className="w-full h-full object-cover"
              style={{ mixBlendMode: "screen" }}
            />
          </div>
          <div className="pt-2">
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              Your tax prep,<br />organized &amp; ready.
            </h1>
            <p className="text-white/80 text-sm mt-2">Save time, stay compliant, and file with confidence.</p>
          </div>
        </div>

        {/* Tagline below logo */}
        <p className="relative z-10 text-[11px] font-bold tracking-[0.22em] uppercase text-white/70 mb-5 pl-1">
          TAX SOFTWARE FOR CHILDCARE CENTERS
        </p>

        {/* Feature pills */}
        <div className="relative z-10 flex flex-wrap gap-2 mb-8">
          <FeaturePill icon={FileCheck} label="CACFP Reports" />
          <FeaturePill icon={Upload} label="OCR Uploads" />
          <FeaturePill icon={Calculator} label="Schedule C & More" />
          <FeaturePill icon={Archive} label="Year-End Archive" />
        </div>

        {/* ── FLOATING LAYERED DASHBOARD MOCKUP ── */}
        <div className="relative z-10 flex-1 min-h-[340px]">

          {/* Card 1 — Dashboard Overview (back) */}
          <div className="absolute top-0 left-0 right-4 bg-white rounded-2xl shadow-2xl p-5 z-10">
            <div className="flex justify-between items-center mb-4">
              <span className="font-bold text-slate-800 text-sm">Dashboard Overview</span>
              <span className="text-[10px] text-slate-500 border border-slate-200 rounded px-2 py-0.5">Tax Year 2025 ▾</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-[10px] text-slate-500 font-medium leading-tight">CACFP Reimbursements</p>
                <p className="text-lg font-bold text-teal-600 mt-1">$42,567</p>
                <p className="text-[9px] text-slate-400">YTD Total</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-[10px] text-slate-500 font-medium leading-tight">Meals Served</p>
                <p className="text-lg font-bold text-slate-800 mt-1">18,642</p>
                <p className="text-[9px] text-slate-400">YTD Total</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 flex flex-col items-center justify-center">
                <div className="relative w-10 h-10">
                  <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#22c55e" strokeWidth="3"
                      strokeDasharray="92.2 100" strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-green-600">98%</span>
                  </div>
                </div>
                <p className="text-[8px] text-slate-500 text-center leading-tight mt-1">Compliance Score</p>
                <p className="text-[8px] text-green-600 font-bold">Excellent</p>
              </div>
            </div>
            {/* Spacer so overlapping cards show below */}
            <div className="h-28" />
          </div>

          {/* Card 2 — Recent Activity (bottom-left, z-20) */}
          <div className="absolute top-44 -left-2 w-52 bg-white rounded-xl shadow-xl p-4 z-20 border border-slate-100">
            <p className="text-xs font-bold text-slate-800 mb-3">Recent Activity</p>
            <div className="space-y-2.5">
              {[
                { emoji: "📄", bg: "bg-blue-50", label: "Receipt_0425.pdf", sub: "Uploaded · Apr 25, 2025" },
                { emoji: "🏦", bg: "bg-green-50", label: "Bank Statement - Apr", sub: "Extracted · Apr 24, 2025" },
                { emoji: "🛒", bg: "bg-amber-50", label: "Office Supplies", sub: "Added · Apr 23, 2025" },
              ].map((a, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded ${a.bg} flex items-center justify-center text-[11px] flex-shrink-0`}>{a.emoji}</div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-700 leading-tight">{a.label}</p>
                    <p className="text-[9px] text-slate-400">{a.sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-blue-500 mt-3 font-medium">View all activity →</p>
          </div>

          {/* Card 3 — Schedule C Summary (center, z-30) */}
          <div className="absolute top-52 left-1/2 -translate-x-14 w-52 bg-white rounded-xl shadow-xl p-4 z-30 border border-slate-100">
            <p className="text-xs font-bold text-slate-800 mb-1">Schedule C Summary</p>
            <p className="text-[10px] text-slate-400 mb-1">Net Profit</p>
            <p className="text-2xl font-black text-teal-600 mb-2">$24,350</p>
            <p className="text-[9px] text-slate-400 mb-2">Tax Year 2025</p>
            {/* Simple line chart */}
            <svg viewBox="0 0 120 40" className="w-full h-10">
              <polyline points="0,35 15,28 30,32 45,20 60,24 75,12 90,8 105,5 120,2"
                fill="none" stroke="#0D9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="0,35 15,28 30,32 45,20 60,24 75,12 90,8 105,5 120,2 120,40 0,40"
                fill="url(#tealFade)" opacity="0.15" />
              <defs>
                <linearGradient id="tealFade" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0D9488" />
                  <stop offset="100%" stopColor="white" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Card 4 — Archives (bottom-right, z-20) */}
          <div className="absolute top-44 right-0 w-44 bg-white rounded-xl shadow-xl p-4 z-20 border border-slate-100">
            <p className="text-xs font-bold text-slate-800 mb-3">Archives</p>
            <div className="flex justify-between text-[9px] text-slate-400 font-semibold mb-2 uppercase tracking-wide">
              <span>Tax Year</span><span>Status</span>
            </div>
            <div className="space-y-2">
              {[
                { yr: "2025", label: "In Progress", cls: "bg-blue-100 text-blue-700" },
                { yr: "2024", label: "Locked", cls: "bg-slate-200 text-slate-600" },
                { yr: "2023", label: "Locked", cls: "bg-slate-200 text-slate-600" },
              ].map((a, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-[11px] font-semibold text-slate-700">{a.yr}</span>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${a.cls}`}>{a.label}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-blue-500 mt-3 font-medium">View all archives →</p>
          </div>
        </div>

        {/* Trust strip */}
        <div className="relative z-10 grid grid-cols-3 gap-4 pt-6 mt-6 border-t border-white/20">
          {[
            { icon: Shield, title: "Secure & Encrypted", copy: "Your data is encrypted and always protected." },
            { icon: FileCheck, title: "Audit-Ready Reports", copy: "Reports built to keep you organized and audit-ready." },
            { icon: Users, title: "Built for Childcare Centers", copy: "Designed by a Childcare Center Owner & Director." },
          ].map((t, i) => (
            <div key={i} className="flex flex-col items-center text-center gap-1">
              <t.icon className="w-5 h-5 text-white/80" />
              <p className="font-bold text-white text-[11px] leading-tight">{t.title}</p>
              <p className="text-white/60 text-[9px] leading-snug">{t.copy}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">

          {/* Badge */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-10 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-bold text-blue-700 text-sm">Built for Childcare Centers</p>
              <p className="text-slate-500 text-xs">Designed by a Childcare Center Owner &amp; Director</p>
            </div>
          </div>

          <h2 className="text-3xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            Welcome back!
          </h2>
          <p className="text-slate-500 mb-8">Sign in to your childcare tax account</p>

          {info && (
            <div data-testid="login-info" className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-lg mb-4">
              {info}
            </div>
          )}
          {error && (
            <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="you@yourcenter.com"
                  required
                  data-testid="login-email-input"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                  className="w-full pl-10 pr-11 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  data-testid="login-remember-checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-blue-600"
                />
                Remember me
              </label>
              <Link to="/forgot-password" data-testid="login-forgot-password-link"
                className="text-sm text-blue-600 hover:text-blue-700 font-semibold">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full text-white font-bold py-3.5 rounded-lg transition-all disabled:opacity-60 text-base"
              style={{ background: "linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)" }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="text-center text-slate-500 text-sm mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-blue-600 font-semibold hover:underline" data-testid="login-register-link">
              Start free trial
            </Link>
          </p>

          <div className="border-t border-slate-100 mt-8 pt-6 text-center space-y-1.5">
            <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3" />
              Your privacy matters. We never share your data.
            </p>
            <p className="text-xs text-slate-400">© 2026 Simple Sign In Tax. All rights reserved.</p>
            <p className="text-[11px] text-slate-400">
              <Link to="/terms" target="_blank" className="hover:text-blue-600 underline" data-testid="login-terms-link">Terms</Link>
              {" · "}
              <Link to="/privacy" target="_blank" className="hover:text-blue-600 underline" data-testid="login-privacy-link">Privacy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturePill({ icon: Icon, label }) {
  return (
    <div className="border border-white/40 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
      <Icon className="w-4 h-4 text-white flex-shrink-0" />
      <span className="text-white text-xs font-semibold">{label}</span>
    </div>
  );
}
